import * as XLSX from 'xlsx';
import { countryRegionMapping } from './country_region_mapping';
import { RegionGroup } from '../types/dataTypes';


export function formatCurrency(value: number) {
    if (value >= 1000000) {
        return `${(value / 1000000).toFixed(2)} Mio`;
    } else if (value >= 1000) {
        return `${Math.floor(value / 1000)} k`;
    } else {
        return value;
    }
}
//utility function to format date in the format dd/mm/yyyy
export function formatDate(date: string) {
    return new Date(date).toLocaleDateString('en-CA');
}

export function formatCurrencyWithValueAndUnit(value: number) : { value: number , unit: string } {
    if (value >= 1000000) {
        return { value: parseFloat((value / 1000000).toFixed(2)), unit: 'Mio' };
    } else if (value >= 1000) {
        return { value: Math.floor(value / 1000), unit: 'k' };
    } else {
        return { value, unit: '' };
    }
}


export function excelDownload(columnNames: string[], data: any[]) {
    // Convert data from array of objects to array of arrays
    const worksheetData = [
        columnNames,
        ...data.map(item => columnNames.map(column => item[column]))
    ];

    // Create a worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Create a workbook and add the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    // Generate a Blob from the workbook
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

    // Create a link element
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'refences_short_all.xlsx';

    // Append the link to the document and trigger the download
    document.body.appendChild(link);
    link.click();

    // Clean up and remove the link
    document.body.removeChild(link);
}

export function getCountriesFromRegions(regions: string[]): string[] {
    
    var countries = countryRegionMapping.filter(region => regions.includes(region.region))
        .flatMap(region => region.country)

        console.log("Countries from regions: ", Array.from(new Set(countries)).sort());

    return Array.from(new Set(countries)).sort();
}

export function getRegionsFromCountries(countries: string[]): any[] {
    const unique = new Map<string, RegionGroup>();
    // Filter the countryRegionMapping to get regions for the provided countries and group by the continent
    var regions = countryRegionMapping.filter(region => countries.includes(region.country))
    
    regions.forEach(({ region, continent }) => {
    const key = region;
    if (!unique.has(key)) {
      unique.set(key, { key, group: continent });
    }
  });

    // Convert to array and sort by group (continent), then region
    return Array.from(unique.values()).sort(
        (a, b) =>
        a.group.localeCompare(b.group) ||
        a.key.localeCompare(b.key)
    );
}

  // Convert file to base64 for upload
export const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

// Convert a raw/base64 image string (as stored/returned by the backend, e.g. Partner's
// profile.logo or Expert's photo) into a data URL an <Avatar>/<img> can render directly.
// Returns null when there's nothing to show, so callers can fall back to a placeholder.
export const getImageSrc = (base64Data: string | null | undefined): string | null => {
  if (!base64Data) return null;

  // If it already starts with data:, return as is
  if (base64Data.startsWith('data:')) {
    return base64Data;
  }

  // If it's raw base64, add the data URL prefix
  // Assuming it's a common image format (you can adjust this based on your needs)
  return `data:image/png;base64,${base64Data}`;
};

export const openExternalLink = (url: string) => {
  if (!url) return;
  
  // Add protocol if missing
  let fullUrl = url;
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    fullUrl = 'https://' + url;
  }
  
  window.open(fullUrl, '_blank');
};


//function to delete the endings of filenames e.g. .pdf, .docx, .xlsx etc.
export const removeFileExtension = (filename: string) => {
    return filename.replace(/\.[^/.]+$/, "");
};

// Best-effort match of a free-text AI-extracted value (e.g. a CV's stated nationality or
// language) against this app's controlled dropdown option list. Exact match first, then a loose
// substring check either direction (handles e.g. "Kenyan" vs "Kenya"). Returns undefined rather
// than guessing when nothing looks close - callers should leave the field for manual selection
// in that case rather than silently picking something wrong (e.g. demonyms like "British" vs
// "United Kingdom" don't share a substring and won't match here - that's intentional caution,
// not a bug to "fix" with more clever matching).
export function bestMatchOption(candidate: string | undefined | null, options: string[]): string | undefined {
    if (!candidate) return undefined;
    const norm = (s: string) => s.trim().toLowerCase();
    const c = norm(candidate);
    if (!c) return undefined;
    const exact = options.find((o) => norm(o) === c);
    if (exact) return exact;
    return options.find((o) => {
        const on = norm(o);
        return on.includes(c) || c.includes(on);
    });
}

export type ContractSizeBracket = {
    label: string;
    rangeLabel: string;
    backgroundColor: string;
    textColor: string;
};

// Buckets a project's Euro value into a size tier for display (e.g. a badge on ReferenceCard).
// Thresholds and labels are a fresh convention for this app - there's no existing bracketing
// scheme anywhere else to match, so pick reasonable round numbers and revisit if the business
// has an official definition later.
const CONTRACT_SIZE_BRACKETS: (ContractSizeBracket & { max: number })[] = [
    { max: 100_000, label: 'Micro <100k', rangeLabel: '<100k', backgroundColor: '#E3F2FD', textColor: '#1565C0' },
    { max: 300_000, label: 'Small 100-300k', rangeLabel: '100-300k', backgroundColor: '#E8F5E9', textColor: '#2E7D32' },
    { max: 750_000, label: 'Medium 300-750k', rangeLabel: '300-750k', backgroundColor: '#FFF8E1', textColor: '#F9A825' },
    { max: 2_000_000, label: 'Large 750k-2m', rangeLabel: '750k-2m', backgroundColor: '#FFF3E0', textColor: '#EF6C00' },
    { max: Infinity, label: 'Flagship >2m', rangeLabel: '>2m', backgroundColor: '#FFEBEE', textColor: '#C62828' },
];

export function getContractSizeBracket(valueEuro: number | null | undefined): ContractSizeBracket | null {
    if (valueEuro === null || valueEuro === undefined || Number.isNaN(valueEuro)) return null;
    const bracket = CONTRACT_SIZE_BRACKETS.find((b) => valueEuro < b.max);
    if (!bracket) return null;
    const { max, ...rest } = bracket;
    return rest;
}